import {skipToken} from '@reduxjs/toolkit/query';
import {Parameter} from 'platform/components';

import {isNotNil} from 'ramda';
import {isNonEmptyString, isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetCustomerV2Query} from '@dms/api/customer';
import {useGetVehicleQuery} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {getCustomerName, useGetVehicleLabels} from '@dms/shared';

import {buildArray, EMPTY_PLACEHOLDER, Nullish} from 'shared/web';

interface AfterSalesHeaderParametersArgs {
  numberLabel: string;
  number: string | Nullish;
  customerLabel: string;
  customerId: string | Nullish;
  vehicleId: string | Nullish;
}

/**
 * Builds the document context strip shown next to the title of the aftersales full-screen
 * baskets - document number, licence plate, make, model and customer. Every value carries
 * its field name as a tooltip and is omitted entirely when it cannot be resolved.
 */
export function useAfterSalesHeaderParameters(args: AfterSalesHeaderParametersArgs): Parameter[] {
  const {data: vehicle} = useGetVehicleQuery(
    isNotNil(args.vehicleId) && isNonEmptyString(args.vehicleId)
      ? {vehicleId: args.vehicleId}
      : skipToken
  );
  const {data: customer} = useGetCustomerV2Query(
    isNotNil(args.customerId) && isNonEmptyString(args.customerId)
      ? {customerId: args.customerId}
      : skipToken
  );
  const [vehicleLabels] = useGetVehicleLabels(
    isNotNil(vehicle)
      ? {make: vehicle.make, modelFamily: vehicle.modelFamily, vehicleType: vehicle.type}
      : null
  );

  const licencePlate = vehicle?.vehicle?.registrationPlate;
  const customerName = getCustomerName(customer);
  // getCustomerName falls back to EMPTY_PLACEHOLDER, which this strip must never render
  const resolvedCustomerName = customerName === EMPTY_PLACEHOLDER ? undefined : customerName;

  return buildArray<Parameter>()
    .when(isNotNilOrEmpty(args.number), {
      title: args.number,
      tooltip: args.numberLabel,
    })
    .when(isNotNilOrEmpty(licencePlate), {
      title: licencePlate,
      tooltip: i18n.t('entity.vehicle.labels.licensePlate'),
    })
    .when(isNotNilOrEmpty(vehicleLabels.make), {
      title: vehicleLabels.make,
      tooltip: i18n.t('entity.vehicle.labels.make'),
    })
    .when(isNotNilOrEmpty(vehicleLabels.modelFamily), {
      title: vehicleLabels.modelFamily,
      tooltip: i18n.t('entity.vehicle.labels.model'),
    })
    .when(isNotNilOrEmpty(resolvedCustomerName), {
      title: resolvedCustomerName,
      tooltip: args.customerLabel,
    });
}
